<template>
  <RevStickyBar
    id="JapanProductPageStickyBarTopRoot"
    class="hidden flex-col md:flex"
    position="top"
    :visible="showBar"
  >
    <RevContainer class="w-full py-1">
      <div class="flex items-center justify-center gap-x-4">
        <div class="flex items-center">
          <RevIllustration
            v-if="productImage"
            :alt="productImage.description"
            class="mr-4"
            :height="51"
            sizes="50vw"
            :src="productImage.url"
            :width="51"
          />
          <p class="body-1-bold">
            {{ title }}
          </p>
        </div>
        <div class="flex grow items-start justify-end gap-x-12">
          <div class="flex flex-col">
            <Price
              :flash-sales-discount
              :mobile-plan="selectedMobilePlan"
              :price
              :product
              :selected-offer
            />
            <PointsSection
              v-if="price && !showPointsSection"
              class="mt-4"
              :only-paypay="true"
              :price
            />
          </div>
          <div class="max-w-256">
            <RevButton
              class="mood-tangaroa"
              data-qa="product-page-buy-button-sticky-bar"
              full-width="always"
              variant="primary"
              @click="onAddToCart"
            >
              {{ i18n(translations.addToCart) }}
            </RevButton>
          </div>
        </div>
      </div>
    </RevContainer>

    <slot name="progress-bar" />
    <TabNavigation
      v-if="showTabNavigation"
      class="border-static-default-low mx-[-24px] -mb-[21px] mt-12 border-t-1"
      :review-count
    />
  </RevStickyBar>
</template>

<script lang="ts" setup>
// fixme: duplicated code due to Japan improved PP
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  watchEffect,
} from 'vue'

import type { Price as PriceType } from '@backmarket/http-api'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { RevStickyBar } from '@ds/components/StickyBar'

import { POSITION_Y_STICKYBAR_DISPLAY } from '~/scopes/product/components/CustomizationFunnel/components/StickyBarTop/StickyBarTop.constants'
import translations from '~/scopes/product/components/CustomizationFunnel/components/StickyBarTop/StickyBarTop.translations'
import { useProductTracking } from '~/scopes/product/composables/useProductTracking'
import { shouldShowPointsSection } from '~/scopes/product/pages/Product-JP/utils/shouldShowPointsSection'
import { useAddToCart } from '~/scopes/product-core/components/ContainerAddToCart/composables/useAddToCart/useAddToCart'
import Price from '~/scopes/product-core/pages/ProductDetailPage/jp/components/JPContainerPrice/components/Price/Price.vue'

import PointsSection from '../PointsSection/PointsSection.vue'
import TabNavigation from '../TabNavigation/TabNavigation.vue'

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  price: PriceType
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  title: string
  tracking: ReturnType<typeof useProductTracking>
  reviewCount: number
}>()

const emit = defineEmits<{
  (event: 'show'): void
  (event: 'hide'): void
}>()

const i18n = useI18n()
const { addToCart } = useAddToCart()
const showBar = ref(false)
const showTabNavigation = ref(false)
const lastYPosition = ref(0)

watchEffect(() => {
  if (showBar.value) {
    emit('show')
  } else {
    emit('hide')
  }
})

const productImage = computed(() => {
  return props.product.images[0] ?? null
})

const flashSalesDiscount = computed(
  () =>
    props.product.promoCodes.find((promoCode) => !!promoCode.isFlashSale)
      ?.discountValue,
)

// We display the points section if the product does not have a discount / priceWhenNew
const showPointsSection = computed(() =>
  shouldShowPointsSection(props.selectedOffer, props.product),
)

function onAddToCart() {
  addToCart({
    listingId: props.selectedOffer.offerLegacyId,
    listingPublicId: props.selectedOffer?.offerId,
    tracking: {
      ...props.tracking.product.value,
      list: 'header',
      campaign_id: props.selectedOffer.campaignId,
      population_id: props.selectedOffer.populationId,
    },
    partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
    isTradeInEligible: props.product.isTradeInEligible,
  })
}

function updateTopBarDisplay() {
  // Since the Title component is displayed one time for mobile and one time for desktop.
  // So the ATC button is appearing twice in the dom.

  const desktopAddToCartCta = document.querySelectorAll(
    '[data-id="product-page-buy-button-desktop"]',
  )?.[0]
  const desktopAddToCartCtaBottomScroll =
    desktopAddToCartCta?.getBoundingClientRect().bottom

  // To have the correct behavior we need to target the first component of the bottom of the page.
  // Reco are not mandatory so if the reco block is failing, we don't have our limit anymore.
  // Fallback would be reviews but we can have products without reviews.
  // Fallback would be the eco block but we have it only for smartphones.
  // Fallback would be SEO tech specs has it's on every page.
  const recoSection = document.querySelector('[data-id="reco-section"]')
  const reviewsSection = document.querySelector('#reviews')
  const seoTechSpecSection = document.querySelector(
    '[data-id="seo-technical-specs"]',
  )
  const bottomPageSection = recoSection || reviewsSection || seoTechSpecSection
  const bottomPageSectionTopScroll =
    bottomPageSection?.getBoundingClientRect().top

  // Mix&Match special rules:
  // Emits show and hide are specific to mix&match
  // hide and show top bar when we are in the funnel or after

  // Show the Sticky bar when ATC is no more visible
  // Show the Sticky bar when scrolling down
  // In mix&match show the Sticky bar when scrolling up passing the reco which represent to end of the funnel.
  if (
    desktopAddToCartCtaBottomScroll &&
    desktopAddToCartCtaBottomScroll <= POSITION_Y_STICKYBAR_DISPLAY &&
    (lastYPosition.value < window.scrollY ||
      (bottomPageSectionTopScroll && bottomPageSectionTopScroll > 0))
  ) {
    showBar.value = true
  }

  // Hide the sticky bar when scrolling up
  if (
    desktopAddToCartCtaBottomScroll > 0 ||
    (bottomPageSectionTopScroll &&
      bottomPageSectionTopScroll <= 0 &&
      lastYPosition.value > window.scrollY)
  ) {
    showBar.value = false
  }

  const tabNavigationInPage = document.getElementById(
    'JapanProductPageTabNavigationInPage',
  )
  const root = document.getElementById('JapanProductPageStickyBarTopRoot')

  if (root && tabNavigationInPage) {
    const scrollDirection = lastYPosition.value > window.scrollY ? 'up' : 'down'
    const referencePosition =
      scrollDirection === 'up'
        ? root.getBoundingClientRect().height -
          tabNavigationInPage.getBoundingClientRect().height
        : root.getBoundingClientRect().height

    showTabNavigation.value =
      tabNavigationInPage.getBoundingClientRect().top < referencePosition
  }

  lastYPosition.value = window.scrollY
}

function handleScroll() {
  if (window.matchMedia('(min-width: 768px)').matches) {
    window.requestAnimationFrame(() => {
      updateTopBarDisplay()
    })
  }
}

// The component generally gets unmounted when changing product, but when we play with the pickers
// we want to keep the bar displayed. Here we make sure to have the right display value when mounting the
// component to avoid repeating the appearance animation of RevStickyBar
onBeforeMount(() => {
  updateTopBarDisplay()
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  emit('hide')
  window.removeEventListener('scroll', handleScroll)
})
</script>
